import Vue from 'vue';
import Vuex from 'vuex';
import VueRouter from 'vue-router';
import VeeValidate from 'vee-validate';
import Vuetify from 'vuetify';
import VueInputAutowidth from 'vue-input-autowidth';
import VueAnalytics from 'vue-analytics';
import VueGtm from 'vue-gtm';
import store from './store';
import theme from './mixins/theme';
import _ from 'lodash';

Vue.use(VueRouter);
Vue.use(Vuex);
Vue.use(VeeValidate);
Vue.use(Vuetify, { theme });
Vue.use(VueInputAutowidth);

/**
 *  Vue components:
 */
import Devices from './components/devices';
import DeviceDetailsModal from './components/device-details-modal';
import DeviceLimitExceeded from './components/device-limit-exceeded';
import DeviceNotFound from './components/device-not-found';
import DeviceOffline from './components/device-offline';
import DevicePairingModal from './components/device-pairing-modal';
import NewGroupModal from './components/new-group-modal';
import Account from './components/account';
import AccountEditModal from './components/account-edit-modal';
import AccountPasswordModal from './components/account-password-modal';
import AccountPaymentModal from './components/account-payment-modal';
import AccountBillingModal from './components/account-billing-modal';
import AccountInvoicesModal from './components/account-invoices-modal';
import AccountAboutModal from './components/account-about-modal';
import LoginModal from './components/login-modal';
import Confirmation from './components/confirmation';
import Confirm from './components/confirm';
import RegistrationModal from './components/registration-modal';
import ForgotPasswordModal from './components/forgot-password-modal';
import ResetPasswordModal from './components/reset-password-modal';
import AppPairing from './components/app-pairing';
import Plans from './components/plans';
import Subscribe from './components/subscribe';
import SvgIcon from 'Components/svg-icon';

/**
 *  Vue Global directives:
 */
import LoadingDirective from './directives/loading';
Vue.directive('loading', LoadingDirective);

import TableDirective from './directives/table';
Vue.directive('table', TableDirective);

/**
 *  Vue Router Settings:
 */
const router = new VueRouter({
	base: '/dashboard/',
	mode: 'history',
	scrollBehavior(to, from, savedPosition) {
		// console.log(to, from);
		// return desired position
	},
	routes: [
		{
			path: '/',
			redirect: 'devices',
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					next('/login');
				}

				next();
			}
		},
		// Dummy page
		{
			path: '/pairing/:pin',
			name: 'app-pairing',
			component: AppPairing,
			beforeEnter: (to, from, next) => {
				store.commit('HIDE_MENU');
				next();
			}
		},
		{
			path: '/account',
			name: 'account',
			meta: { title: 'Profile - Webkey Dashboard' },
			component: Account,
			children: [
				{
					path: 'edit',
					name: 'account-edit-modal',
					meta: { title: 'Edit Account Details - Webkey Dashboard' },
					component: AccountEditModal
				},
				{
					path: 'password',
					name: 'account-password-modal',
					meta: { title: 'Change Password - Webkey Dashboard' },
					component: AccountPasswordModal
				},
				{
					path: 'payment',
					name: 'account-payment-modal',
					meta: { title: 'Update Payment Method - Webkey Dashboard' },
					component: AccountPaymentModal
				},
				{
					path: 'billing',
					name: 'account-billing-modal',
					meta: { title: 'Change Billing Details - Webkey Dashboard' },
					component: AccountBillingModal
				},
				{
					path: 'invoices',
					name: 'account-invoices-modal',
					meta: { title: 'Invoices - Webkey Dashboard' },
					component: AccountInvoicesModal
				},
				{
					path: 'about',
					name: 'account-about-modal',
					meta: { title: 'About - Webkey Dashboard' },
					component: AccountAboutModal
				}
			],
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					next('/login');
				}

				next();
			}
		},
		{
			path: '/legal',
			beforeEnter(to, from, next) {
				window.location = '/terms/';
			}
		},
		{
			path: '/devices',
			name: 'devices',
			meta: { title: 'My Devices - Webkey Dashboard' },
			component: Devices,
			children: [
				{
					path: ':publicID',
					name: 'device-details-modal',
					meta: { title: 'Device Details - Webkey Dashboard' },
					component: DeviceDetailsModal
				},
				{
					path: '/pairing',
					name: 'pairing-device',
					meta: { title: 'Device Pairing - Webkey Dashboard' },
					component: DevicePairingModal,
					beforeEnter: (to, from, next) => {
						if (store.state.user.status === 'cancelled') {
							next(`${from.path}`);
						}

						next();
					}
				},
				{
					path: 'group',
					name: 'new-group-modal',
					meta: { title: 'NewGroup - Webkey Dashboard' },
					component: NewGroupModal,
					beforeEnter: (to, from, next) => {
						if (!store.state.user.isLoggedIn) {
							next(`/login`);
						}
						next();
					}
				}
			],
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					next('/login');
				}
				if (store.state.user.status === 'cancelled') {
					next('/plans');
				}
				next();
			}
		},
		{
			path: '/device-limit-exceeded',
			name: 'device-limit-exceeded',
			meta: { title: 'Device Limit Exceeded - Webkey Dashboard' },
			component: DeviceLimitExceeded,
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					next(`/login?redirect=${to.path}`);
				}
				next();
			}
		},
		{
			path: '/device-not-found/:deviceNick',
			name: 'device-not-found',
			meta: {
				title: 'Device Not Found - Webkey Dashboard',
				analytics: {
					pageviewTemplate (route) {
						return {
							page: '/device-not-found/DEVICENICK'
						}
					}
				}
			},
			component: DeviceNotFound
		},
		{
			path: '/device-offline/:deviceReference',
			name: 'device-offline',
			meta: {
				title: 'Device Offline - Webkey Dashboard',
				analytics: {
					pageviewTemplate (route) {
						return {
							page: '/device-offline/DEVICEREFERENCE'
						}
					}
				}
			},
			component: DeviceOffline,
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					next(`/login?redirect=${to.path}`);
				}
				next();
			}
		},
		{
			path: '/login/:email?',
			name: 'login',
			meta: { title: 'Login - Webkey Dashboard' },
			components: {
				modal: LoginModal
			},
			beforeEnter: (to, from, next) => {
				store.commit('HIDE_MENU');
				next();
			}
		},
		{
			path: '/confirm-account',
			name: 'confirmation',
			meta: { title: 'Confirmation - Webkey Dashboard' },
			components: {
				modal: Confirmation
			},
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					next('/login');
				}
				if (store.state.user.isConfirmed) {
					next('/');
				} else {
					store.commit('HIDE_MENU');
				}
				next();
			}
		},
		{
			path: '/confirm-account/:token',
			name: 'confirm',
			meta: {
				analytics: {
					pageviewTemplate (route) {
						return {
							page: '/confirm-account/CONFIRMTOKEN'
						}
					}
				}
			},
			component: Confirm,
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					next(`/login?redirect=${to.path}`);
				}

				store.commit('HIDE_MENU');
				next();
			}
		},
		{
			path: '/logout',
			name: 'logout',
			beforeEnter: (to, from, next) => {
				if (store.state.user.isLoggedIn) {
					store.commit('LOGOUT_USER');
					store.commit('UPDATE_DEVICES', { Devices: [], TotalDevices: null, Groups: [] });
					store.commit('UPDATE_MAX_DEVICES', null);
				}

				next('/login');
			}
		},
		{
			path: '/registration/:email?',
			name: 'registration',
			meta: { title: 'Registration - Webkey Dashboard' },
			components: {
				modal: RegistrationModal
			},
			beforeEnter: (to, from, next) => {
				store.commit('HIDE_MENU');
				next();
			}
		},
		{
			path: '/forgot-password/:email?',
			name: 'forgot-password',
			meta: { title: 'Forgot Password - Webkey Dashboard' },
			components: {
				modal: ForgotPasswordModal
			}
		},
		{
			path: '/reset-password/:token',
			name: 'reset-password',
			meta: { title: 'Reset Password - Webkey Dashboard' },
			components: {
				modal: ResetPasswordModal
			}
		},
		{
			path: '/plans',
			name: 'plans',
			meta: { title: 'Plans - Webkey Dashboard' },
			components: {
				modal: Plans
			},
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					next(`/login?redirect=${to.path}`);
				}
				next();
			}
		},
		{
			path: '/subscription/plan/:planName',
			name: 'subscribe',
			meta: { title: 'Subscription - Webkey Dashboard' },
			components: {
				modal: Subscribe
			},
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					router.replace({
						name: `registration`,
						query: {
							redirect: `${to.path}`,
							plan: `${to.params.planName}`
						}
					});
				}
				store.commit('HIDE_MENU');
				next();
			}
		},
		{
			path: '/success',
			name: 'success',
			meta: { title: 'My Devices - Webkey Dashboard' },
			component: Devices,
			beforeEnter: (to, from, next) => {
				if (!store.state.user.isLoggedIn) {
					next('/login');
				}
				next();
			}
		}
	]
});

router.beforeEach((to, from, next) => {
	if (to.meta.title === undefined) {
		document.title = 'Webkey Dashboard';
	} else {
		document.title = to.meta.title;
	}
	store.commit('SHOW_MENU');
	next();
});

Vue.use(VueGtm, {
	id: 'GTM-PC8W5PG',
	enabled: isGTMEnabled(),
	debug: true,
	vueRouter: false
});

Vue.use(VueAnalytics, {
	id: 'UA-67536410-4',
	checkDuplicatedScript: true,
	disabled: !isGoogleAnalyticsEnabled(),
	router,
	ecommerce: {
		enabled: true
	},
	set: [
		{ field: 'anonymizeIp', value: true }
	]
});

function isGoogleAnalyticsEnabled() {
	return process.env.GOOGLE_ANALYTICS === 'true';
}

function isGTMEnabled() {
	return process.env.GOOGLE_TAG_MANAGER === 'true';
}

Vue.filter('deviceAccessLevel', function(device) {
	if (!device || _.isEmpty(device)) return '';

	var accessLevel = 'Non rooted';
	if (device.Build.FlavorGlobal === 'systemsigned') {
		accessLevel = 'Signed APK';
	} else if (device.Rooted) {
		accessLevel = 'Rooted';
	}

	return accessLevel;
});

Vue.filter('groupName', function(groupID) {
	var groups = store.state.devices.groups;
	var name = 'All devices';

	groups.forEach(group => {
		if (groupID === group.ID.toString()) {
			name = group.Name;
		}
	});

	return name;
});

function getAssetsSrc(file) {
	if (process.env.CDN_PATH) {
		return process.env.CDN_PATH + file;
	}
	return process.env.LOCAL_PATH + file;
}

Vue.mixin({
	components: {
		SvgIcon
	},
	mounted() {
		this.onResize();
	},
	data: () => ({
		windowSize: {
			width: 0,
			height: 0
		}
	}),
	methods: {
		getAssetsSrc(file) {
			return getAssetsSrc(file);
		},
		onResize() {
			this.windowSize = { width: window.innerWidth, height: window.innerHeight };
		}
	}
});

/**
 *  Event Hub:
 *  (See https://vuejs.org/guide/migration.html#dispatch-and-broadcast-deprecated for details.)
 */
export const mainEventHub = new Vue();

/* eslint-disable no-new */
const app = new Vue({
	router,
	store,
	vuetify: new Vuetify({ theme }),
	data: {
		domain: process.env.SITE_URL,
		isMerchantEnabled: process.env.FEATURE_MERCHANT === 'true',
		isGoogleAnalyticsEnabled: process.env.GOOGLE_ANALYTICS === 'true',
		drawer: false
	},
	computed: {
		isLoggedIn() {
			return this.$store.state.user.isLoggedIn;
		},
		isConfirmed() {
			return this.$store.state.user.isConfirmed;
		},
		isMenuVisible() {
			return this.$store.state.menu.isVisible;
		},
		isUpgradeVisible() {
			return (
				this.$store.state.user.status === 'in_trial' ||
				this.$store.state.user.status === 'cancelled' ||
				this.$store.state.user.plan.name === 'veteran' ||
				this.$store.state.user.plan.name === 'free'
			);
		},
		isDevicesStatusVisible() {
			return (
				this.$store.state.devices.total !== null &&
				this.$store.state.devices.max !== null &&
				this.$store.state.devices.max !== '-1' &&
				this.$store.state.user.status !== 'cancelled'
			);
		},
		devicesLink() {
			return 'My devices(' + this.$store.state.devices.total + '/' + this.$store.state.devices.max + ')';
		},
		isSubscriptionExpired() {
			return this.$store.state.user.status === 'cancelled';
		}
	}
}).$mount('#app');

export default app;



// WEBPACK FOOTER //
// ./app/dashboard.js